import React from 'react';
import {
  Box,
  Button as MuiButton,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { X } from 'lucide-react';
import { Button } from '../../../../../../ui';
import {
  DocumentConfiguration,
  DocumentRequirementStatus,
} from '../../../../../../utils/types/JobOpenings';

export const DEFAULT_DOCUMENTS = [
  {
    type: 'Resume / CV',
    description:
      'A standard professional summary of work experience and education.',
    mandatoryFixed: true,
  },
  {
    type: 'Cover Letter',
    description:
      "An introductory letter explaining the candidate's motivation.",
  },
  {
    type: 'Academic Transcript',
    description:
      'Official records showing completed courses and grades from educational institutions.',
  },
  {
    type: 'Tertiary Certificates',
    description:
      'Certificates obtained from higher education institutions like universities or colleges.',
  },
  {
    type: 'Professional Certifications',
    description:
      'Industry-specific certificates or professional licenses demonstrating expertise.',
  },
  {
    type: 'Reference Letters',
    description:
      'Letters from former employers or colleagues providing professional recommendations.',
  },
  {
    type: 'Work Portfolio',
    description:
      'Samples of previous projects, case studies, or links to individual repositories.',
  },
];

interface DocumentConfigurationModalProps {
  isOpen: boolean;
  onClose: () => void;
  editable: boolean;
  documentConfigurations: DocumentConfiguration[];
  onDocumentRequirementChange: (
    type: string,
    requirement: DocumentRequirementStatus,
  ) => void;
  onAllowMultipleChange: (type: string, allowMultiple: boolean) => void;
  onMakeAllMandatory: () => void;
  onMakeAllOptional: () => void;
  onMakeAllDisabled: () => void;
}

export const DocumentConfigurationModal: React.FC<DocumentConfigurationModalProps> = ({
  isOpen,
  onClose,
  editable,
  documentConfigurations,
  onDocumentRequirementChange,
  onAllowMultipleChange,
  onMakeAllMandatory,
  onMakeAllOptional,
  onMakeAllDisabled,
}) => {
  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
        },
      }}
    >
      {/* Modal Header */}
      <DialogTitle sx={{ px: 3, py: 2, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
              Required Documents Configuration
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
              Define which documents candidates must provide and their requirements.
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <X size={20} />
          </IconButton>
        </Stack>
      </DialogTitle>

      {/* Modal Body */}
      <DialogContent sx={{ p: 3 }}>
        <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: 'grey.50' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Document
                </TableCell>
                {(['Mandatory', 'Optional', 'Disabled'] as const).map((status) => (
                  <TableCell key={status} sx={{ width: 120, py: 1 }}>
                    <MuiButton
                      size="small"
                      disabled={!editable}
                      onClick={() => {
                        if (status === 'Mandatory') onMakeAllMandatory();
                        else if (status === 'Optional') onMakeAllOptional();
                        else onMakeAllDisabled();
                      }}
                      sx={{
                        fontSize: '11px',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        justifyContent: 'flex-start',
                        p: 0.5,
                        color:
                          status === 'Mandatory'
                            ? 'success.main'
                            : status === 'Optional'
                            ? 'primary.main'
                            : 'text.secondary',
                      }}
                    >
                      ● {status === 'Disabled' ? 'Disable' : status}
                    </MuiButton>
                  </TableCell>
                ))}
                <TableCell sx={{ fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', width: 120 }}>
                  Allow Multiple
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {DEFAULT_DOCUMENTS.map((doc, idx) => {
                const config = documentConfigurations?.find(
                  (c) => c.type === doc.type,
                );
                const requirement: DocumentRequirementStatus =
                  config?.requirement || 'Disabled';
                const isLocked = !!(doc as any).mandatoryFixed;
                const isDisabled = !editable || isLocked;
                const allowsMultipleToggle =
                  doc.type !== 'Resume / CV' && doc.type !== 'Cover Letter';
                const allowMultiple = config?.allowMultiple ?? false;

                return (
                  <TableRow
                    key={idx}
                    sx={{
                      bgcolor: idx % 2 === 0 ? 'background.paper' : 'grey.50',
                      opacity: requirement === 'Disabled' ? 0.6 : 1,
                    }}
                  >
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: requirement === 'Disabled' ? 'text.secondary' : 'text.primary',
                          }}
                        >
                          {doc.type}
                        </Typography>
                        {isLocked && (
                          <Chip
                            label="Fixed"
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={{ height: 18, fontSize: '9px', fontWeight: 700 }}
                          />
                        )}
                      </Stack>
                    </TableCell>

                    {/* Mandatory */}
                    <TableCell>
                      <FormControlLabel
                        value="Mandatory"
                        control={
                          <Radio
                            size="small"
                            color="success"
                            checked={requirement === 'Mandatory'}
                            disabled={isDisabled}
                            onChange={() =>
                              onDocumentRequirementChange(doc.type, 'Mandatory')
                            }
                            sx={{ p: 0.5 }}
                          />
                        }
                        label={
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: requirement === 'Mandatory' ? 700 : 400,
                              color: requirement === 'Mandatory' ? 'success.dark' : 'text.secondary',
                            }}
                          >
                            Mandatory
                          </Typography>
                        }
                        sx={{ m: 0, gap: 0.5 }}
                      />
                    </TableCell>

                    {/* Optional */}
                    <TableCell>
                      <FormControlLabel
                        value="Optional"
                        control={
                          <Radio
                            size="small"
                            color="primary"
                            checked={requirement === 'Optional'}
                            disabled={isDisabled}
                            onChange={() =>
                              onDocumentRequirementChange(doc.type, 'Optional')
                            }
                            sx={{ p: 0.5 }}
                          />
                        }
                        label={
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: requirement === 'Optional' ? 700 : 400,
                              color: requirement === 'Optional' ? 'primary.main' : 'text.secondary',
                            }}
                          >
                            Optional
                          </Typography>
                        }
                        sx={{ m: 0, gap: 0.5 }}
                      />
                    </TableCell>

                    {/* Disabled */}
                    <TableCell>
                      <FormControlLabel
                        value="Disabled"
                        control={
                          <Radio
                            size="small"
                            color="default"
                            checked={requirement === 'Disabled'}
                            disabled={isDisabled}
                            onChange={() =>
                              onDocumentRequirementChange(doc.type, 'Disabled')
                            }
                            sx={{ p: 0.5 }}
                          />
                        }
                        label={
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: requirement === 'Disabled' ? 700 : 400,
                              color: requirement === 'Disabled' ? 'text.primary' : 'text.secondary',
                            }}
                          >
                            Disabled
                          </Typography>
                        }
                        sx={{ m: 0, gap: 0.5 }}
                      />
                    </TableCell>

                    {/* Allow Multiple Toggle */}
                    <TableCell>
                      {allowsMultipleToggle ? (
                        <Switch
                          size="small"
                          color="success"
                          checked={allowMultiple}
                          disabled={!editable || requirement === 'Disabled'}
                          onChange={() =>
                            onAllowMultipleChange(doc.type, !allowMultiple)
                          }
                        />
                      ) : (
                        <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                          —
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      {/* Modal Footer */}
      <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
        <Button onClick={onClose} variant="secondary" label="Close" sx={{ px: 3 }} />
      </DialogActions>
    </Dialog>
  );
};

export default DocumentConfigurationModal;
